import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { useTheme } from '../context/ThemeContext'
import { Upload, File, CheckCircle, Loader, Info, ChevronDown, Image, Box, Download } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import axios from 'axios'
import AddObjectToProblemSetModal from '../components/AddObjectToProblemSetModal'

const Container = styled.div`
  padding: 40px;
  background-color: ${props => props.theme.backgroundColor};
  min-height: calc(100vh - 80px);
  width: 100%;

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 24px;
`

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`

const Subtitle = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 0;
`

const InfoBox = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  text-align: left;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`

const InfoTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`

const InfoText = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 0 0 6px 0;
  line-height: 1.5;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    font-size: 13px;
  }
`

const InfoHighlight = styled.span`
  color: ${props => props.theme.buttonPrimary};
  font-weight: 600;
`

const SelectionSection = styled.div`
  margin-bottom: 30px;
`

const SectionLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 12px;
`

const SelectBox = styled.select`
  width: 100%;
  padding: 14px 16px;
  font-size: 16px;
  color: ${props => props.theme.textPrimary};
  background-color: ${props => props.theme.cardBackground};
  border: 2px solid ${props => props.theme.border};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.theme.buttonPrimary};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  option {
    padding: 10px;
  }
`

const ObjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 20px;
`

const ObjectCard = styled.div`
  position: relative;
  background-color: ${props => props.theme.cardBackground};
  border: 3px solid ${props => props.$selected ? props.theme.buttonPrimary : props.theme.border};
  border-radius: 16px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &:hover {
    border-color: ${props => props.theme.buttonPrimary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  ${props => props.$selected && `
    box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
  `}
`

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const PreviewLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.theme.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: left;
`

const ObjectImage = styled.div`
  width: 100%;
  height: 120px;
  background-image: url(${props => props.$image || 'https://via.placeholder.com/150'});
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  border: 2px solid ${props => props.theme.border};
`

const ModelPreview = styled.div`
  width: 100%;
  height: 120px;
  border-radius: 12px;
  border: 2px solid ${props => props.theme.border};
  background-color: ${props => props.theme.cardBackground};

  model-viewer {
    width: 100%;
    height: 100%;
    border-radius: 12px;
  }
`

const NoModelBadge = styled.div`
  width: 100%;
  height: 120px;
  border-radius: 12px;
  border: 2px dashed ${props => props.theme.border};
  background-color: ${props => props.theme.border};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textSecondary};
  font-size: 12px;
  font-weight: 600;
`

const ObjectName = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin: 0;
  word-break: break-word;
`

const ObjectId = styled.p`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  margin: 4px 0 0 0;
`

const UploadArea = styled.div`
  border: 3px dashed ${props => props.$isDragOver ? props.theme.buttonPrimary : props.theme.border};
  border-radius: 20px;
  padding: 60px 40px;
  text-align: center;
  background-color: ${props => props.$isDragOver ? props.theme.hoverBackground : props.theme.cardBackground};
  transition: all 0.3s ease;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  margin-bottom: 30px;
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:hover {
    background-color: ${props => props.$disabled ? props.theme.cardBackground : props.theme.hoverBackground};
    border-color: ${props => props.$disabled ? props.theme.border : props.theme.buttonPrimary};
  }

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`

const UploadIcon = styled(Upload)`
  width: 64px;
  height: 64px;
  color: ${props => props.theme.buttonPrimary};
  margin: 0 auto 20px;

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
  }
`

const UploadText = styled.p`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 10px 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`

const UploadSubtext = styled.p`
  font-size: 16px;
  color: ${props => props.theme.textSecondary};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`

const HiddenInput = styled.input`
  display: none;
`

const FileList = styled.div`
  margin-top: 30px;
`

const FileItem = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  flex: 1;
  min-width: 0;
`

const FileIcon = styled(File)`
  width: 32px;
  height: 32px;
  color: ${props => props.theme.buttonPrimary};
  flex-shrink: 0;
`

const FileDetails = styled.div`
  flex: 1;
  min-width: 0;
`

const FileName = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 5px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const FileSize = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 0 0 5px 0;
`

const ProgressInfo = styled.p`
  font-size: 12px;
  color: ${props => props.theme.buttonPrimary};
  margin: 5px 0 0 0;
  font-weight: 600;
`

const StatusIcon = styled.div`
  flex-shrink: 0;
`

const UploadButton = styled.button`
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px 40px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  margin-top: 20px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(99, 102, 241, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 14px 30px;
    font-size: 16px;
  }
`

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${props => props.theme.border};
  border-radius: 4px;
  overflow: hidden;
  margin-top: 10px;
`

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`

const ResultMessage = styled.div`
  padding: 20px;
  border-radius: 12px;
  margin-top: 20px;
  background-color: ${props => props.$success ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
  border: 2px solid ${props => props.$success ? '#22c55e' : '#ef4444'};
  color: ${props => props.$success ? '#22c55e' : '#ef4444'};
  font-weight: 600;
  text-align: center;
`

const ResultUrl = styled.a`
  display: block;
  margin-top: 10px;
  color: ${props => props.theme.buttonPrimary};
  text-decoration: none;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
  }
`

const ChunkInfo = styled.div`
  margin-top: 10px;
  padding: 15px;
  background-color: ${props => props.theme.border};
  border-radius: 8px;
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  text-align: left;

  > div {
    margin-bottom: 8px;
  }
`

const LoadingSpinner = styled(Loader)`
  animation: spin 1s linear infinite;
  margin: 20px auto;
  display: block;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`

const EmptyMessage = styled.p`
  text-align: center;
  color: ${props => props.theme.textSecondary};
  font-size: 16px;
  padding: 40px 20px;
`

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  border-bottom: 1px solid ${props => props.theme.border};
  overflow-x: auto;
`

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  background: none;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 3px solid transparent;
  color: ${props => props.$active ? props.theme.buttonPrimary : props.theme.textSecondary};
  font-weight: ${props => props.$active ? '600' : '400'};
  white-space: nowrap;

  &:hover {
    color: ${props => props.theme.buttonPrimary};
    background-color: ${props => props.theme.hoverBackground};
  }

  ${props => props.$active && `
    border-bottom-color: ${props.theme.buttonPrimary};
    background-color: ${props.theme.hoverBackground};
  `}

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
  }
`

const AddObjectCard = styled(ObjectCard)`
  border: 3px dashed ${props => props.theme.buttonPrimary};
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;

  &:hover {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  }
`

const AddIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.theme.buttonPrimary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 300;
  margin-bottom: 12px;
`

const AddText = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.buttonPrimary};
  margin: 0;
`

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`

const ModalContent = styled.div`
  background: ${props => props.theme.backgroundColor};
  border-radius: 20px;
  padding: 40px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 24px 0;
`

const InputLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 8px;
`

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  font-size: 16px;
  color: ${props => props.theme.textPrimary};
  background-color: ${props => props.theme.cardBackground};
  border: 2px solid ${props => props.theme.border};
  border-radius: 12px;
  margin-bottom: 20px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  font-size: 16px;
  color: ${props => props.theme.textPrimary};
  background-color: ${props => props.theme.cardBackground};
  border: 2px solid ${props => props.theme.border};
  border-radius: 12px;
  margin-bottom: 20px;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`

const Button = styled.button`
  flex: 1;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;

  ${props => props.$primary ? `
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(99, 102, 241, 0.4);
    }
  ` : `
    background: ${props.theme.border};
    color: ${props.theme.textSecondary};

    &:hover:not(:disabled) {
      background: ${props.theme.hoverBackground};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

function ObjectUploadPage() {
  const { theme } = useTheme()
  const { user } = useAuth()
  const [selectedFile, setSelectedFile] = useState(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(null)
  const [uploadId, setUploadId] = useState(null)
  const fileInputRef = useRef(null)
  const progressIntervalRef = useRef(null)

  // 탭 상태
  const [activeTab, setActiveTab] = useState('2d') // '2d' or '3d'

  // 3D 업로드 - 문제세트 관련 상태
  const [problemSets, setProblemSets] = useState([])
  const [selectedProblemSet, setSelectedProblemSet] = useState(null)
  const [arObjects, setArObjects] = useState([])
  const [selectedObject, setSelectedObject] = useState(null)
  const [loading, setLoading] = useState(true)

  // 오브젝트 추가 모달 상태
  const [showAddObjectModal, setShowAddObjectModal] = useState(false)
  const [selectedProblemSetForAdd, setSelectedProblemSetForAdd] = useState(null)

  // 2D 업로드 - 카테고리 관련 상태
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [categoryObjects, setCategoryObjects] = useState([])
  const [selectedCategoryObject, setSelectedCategoryObject] = useState(null)
  const [categoryLoading, setCategoryLoading] = useState(false)

  // 새 오브젝트 추가 모달 상태
  const [showAddModal, setShowAddModal] = useState(false)
  const [newObjectName, setNewObjectName] = useState('')
  const [newObjectDescription, setNewObjectDescription] = useState('')
  const [creatingObject, setCreatingObject] = useState(false)

  // 새 카테고리 추가 모달 상태
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [newCategoryDescription, setNewCategoryDescription] = useState('')
  const [creatingCategory, setCreatingCategory] = useState(false)

  // 페르소나 프롬프트 편집 모달 상태
  const [showPersonaModal, setShowPersonaModal] = useState(false)
  const [personaPrompt, setPersonaPrompt] = useState('')
  const [savingPersona, setSavingPersona] = useState(false)

  // 디버깅용
  useEffect(() => {
    console.log('📦 problemSets 상태 변경:', problemSets)
    console.log('⏳ loading 상태:', loading)
  }, [problemSets, loading])

  // 카테고리 목록 불러오기 (2D 업로드용)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoryLoading(true)
        const response = await axios.get('/ObjectUpload/categories')

        if (response.data.success) {
          console.log('✅ 카테고리 목록:', response.data.categories)
          setCategories(response.data.categories)
        }
      } catch (error) {
        console.error('❌ 카테고리 불러오기 실패:', error)
      } finally {
        setCategoryLoading(false)
      }
    }

    if (activeTab === '2d') {
      fetchCategories()
    }
  }, [activeTab])

  // 카테고리별 오브젝트 목록 불러오기
  useEffect(() => {
    const fetchCategoryObjects = async () => {
      if (!selectedCategory) {
        setCategoryObjects([])
        setSelectedCategoryObject(null)
        return
      }

      try {
        const response = await axios.get(`/ObjectUpload/objects-by-category/${selectedCategory}`)

        if (response.data.success) {
          console.log('✅ 오브젝트 목록:', response.data.objects)
          setCategoryObjects(response.data.objects)
        }
      } catch (error) {
        console.error('❌ 오브젝트 불러오기 실패:', error)
      }
    }

    fetchCategoryObjects()
  }, [selectedCategory])

  // 내 문제세트 목록 불러오기 (3D 업로드용)
  useEffect(() => {
    const fetchMyProblemSets = async () => {
      try {
        console.log('🔍 문제세트 목록 불러오기 시작, user:', user)
        setLoading(true)
        const response = await fetch('/auth/get-my-quizzes', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        })

        console.log('📡 API 응답 상태:', response.status)

        if (!response.ok) {
          throw new Error('문제세트 목록을 불러올 수 없습니다.')
        }

        const data = await response.json()
        console.log('✅ 내 문제세트 목록 응답:', data)

        // API 응답이 data.myQuizzes로 옴
        const quizzes = data.myQuizzes || data.quizzes || []
        console.log('📊 퀴즈 개수:', quizzes.length)

        if (data.success && quizzes.length > 0) {
          console.log('💾 setProblemSets 호출:', quizzes)
          setProblemSets(quizzes)
        } else {
          console.warn('⚠️ 데이터 형식 문제 또는 퀴즈 없음:', data)
          setProblemSets([])
        }
      } catch (error) {
        console.error('❌ 문제세트 불러오기 실패:', error)
      } finally {
        setLoading(false)
      }
    }

    if (activeTab === '3d') {
      console.log('🚀 useEffect 실행, user:', user)
      fetchMyProblemSets()
    }
  }, [activeTab])

  // 문제세트 선택 시 오브젝트 목록 불러오기
  useEffect(() => {
    const fetchProblemSetDetail = async () => {
      if (!selectedProblemSet) {
        setArObjects([])
        setSelectedObject(null)
        return
      }

      try {
        const response = await fetch(`/auth/get-quiz-detail/${selectedProblemSet}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error('문제세트 상세 정보를 불러올 수 없습니다.')
        }

        const data = await response.json()
        console.log('문제세트 상세:', data)

        if (data.success && data.quiz && data.quiz.problems) {
          // AR 오브젝트 목록 추출 (중복 제거)
          const objectsMap = new Map()
          data.quiz.problems.forEach(problem => {
            const objectId = problem.ar_object_id
            const objectName = problem.ar_object_name || problem.arObject || '알 수 없는 오브젝트'
            const objectImage = problem.ar_object_image
            const modelUrl = problem.model_url || null
            const personaPrompt = problem.persona_prompt || null

            if (objectId && !objectsMap.has(objectId)) {
              objectsMap.set(objectId, {
                id: objectId,
                name: objectName,
                image: objectImage,
                modelUrl: modelUrl,
                persona_prompt: personaPrompt
              })
            }
          })

          const uniqueObjects = Array.from(objectsMap.values())
          console.log('고유 오브젝트 목록:', uniqueObjects)
          setArObjects(uniqueObjects)
        }
      } catch (error) {
        console.error('문제세트 상세 불러오기 실패:', error)
      }
    }

    fetchProblemSetDetail()
  }, [selectedProblemSet])

  // 진행률 폴링
  useEffect(() => {
    if (uploading && uploadId) {
      progressIntervalRef.current = setInterval(async () => {
        try {
          const response = await axios.get(`/ObjectUpload/progress/${uploadId}`)
          if (response.data.success) {
            setUploadProgress(response.data.progress)
          }
        } catch (error) {
          console.error('진행률 조회 실패:', error)
        }
      }, 500)

      return () => {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current)
        }
      }
    }
  }, [uploading, uploadId])

  const handleFileSelect = (file) => {
    setSelectedFile(file)
    setUploadResult(null)
    setUploadProgress(null)
    setUploadId(null)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    if (!selectedObject) return
    setIsDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)

    if (!selectedObject) return

    const file = e.dataTransfer.files[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleFileInputChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  // 오브젝트 삭제 핸들러
  const handleDeleteObject = async (objectId, objectName) => {
    if (!window.confirm(`"${objectName}" 오브젝트를 삭제하시겠습니까?`)) {
      return
    }

    try {
      const response = await axios.delete(`/ObjectUpload/delete-object/${objectId}`)

      if (response.data.success) {
        console.log('✅ 오브젝트 삭제 성공:', response.data.data)
        alert('오브젝트가 삭제되었습니다.')

        // 오브젝트 목록 새로고침
        const refreshResponse = await axios.get(`/ObjectUpload/objects-by-category/${selectedCategory}`)
        if (refreshResponse.data.success) {
          setCategoryObjects(refreshResponse.data.objects)
        }

        // 삭제된 오브젝트가 선택되어 있었다면 초기화
        if (selectedCategoryObject?.object_id === objectId) {
          setSelectedCategoryObject(null)
          setSelectedFile(null)
        }
      }
    } catch (error) {
      console.error('❌ 오브젝트 삭제 실패:', error)
      alert('오브젝트 삭제 실패: ' + (error.response?.data?.message || error.message))
    }
  }

  // 카테고리 삭제 핸들러
  const handleDeleteCategory = async () => {
    if (!selectedCategory) return

    const categoryName = categories.find(cat => cat.category_id === selectedCategory)?.name
    if (!window.confirm(`"${categoryName}" 카테고리를 삭제하시겠습니까?`)) {
      return
    }

    try {
      const response = await axios.delete(`/ObjectUpload/delete-category/${selectedCategory}`)

      if (response.data.success) {
        console.log('✅ 카테고리 삭제 성공:', response.data.data)
        alert('카테고리가 삭제되었습니다.')

        // 카테고리 목록 새로고침
        const refreshResponse = await axios.get('/ObjectUpload/categories')
        if (refreshResponse.data.success) {
          setCategories(refreshResponse.data.categories)
        }

        // 선택된 카테고리 초기화
        setSelectedCategory(null)
        setCategoryObjects([])
        setSelectedCategoryObject(null)
        setSelectedFile(null)
      }
    } catch (error) {
      console.error('❌ 카테고리 삭제 실패:', error)
      alert('카테고리 삭제 실패: ' + (error.response?.data?.message || error.message))
    }
  }

  // 새 카테고리 생성 핸들러
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return

    setCreatingCategory(true)

    try {
      const response = await axios.post('/ObjectUpload/create-category', {
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim() || null
      })

      if (response.data.success) {
        console.log('✅ 새 카테고리 생성 성공:', response.data.data)

        // 카테고리 목록 새로고침
        const refreshResponse = await axios.get('/ObjectUpload/categories')
        if (refreshResponse.data.success) {
          setCategories(refreshResponse.data.categories)
        }

        // 모달 닫기 및 상태 초기화
        setShowAddCategoryModal(false)
        setNewCategoryName('')
        setNewCategoryDescription('')

        // 생성된 카테고리 자동 선택
        setSelectedCategory(response.data.data.category_id)
      }
    } catch (error) {
      console.error('❌ 새 카테고리 생성 실패:', error)
      alert('카테고리 생성에 실패했습니다: ' + (error.response?.data?.message || error.message))
    } finally {
      setCreatingCategory(false)
    }
  }

  // 새 오브젝트 생성 핸들러
  const handleCreateObject = async () => {
    if (!newObjectName.trim() || !selectedCategory) return

    setCreatingObject(true)

    try {
      const response = await axios.post('/ObjectUpload/create-object', {
        categoryId: selectedCategory,
        name: newObjectName.trim(),
        description: newObjectDescription.trim() || null
      })

      if (response.data.success) {
        console.log('✅ 새 오브젝트 생성 성공:', response.data.data)

        // 오브젝트 목록 새로고침
        const refreshResponse = await axios.get(`/ObjectUpload/objects-by-category/${selectedCategory}`)
        if (refreshResponse.data.success) {
          setCategoryObjects(refreshResponse.data.objects)
        }

        // 모달 닫기 및 상태 초기화
        setShowAddModal(false)
        setNewObjectName('')
        setNewObjectDescription('')

        // 생성된 오브젝트 자동 선택
        setSelectedCategoryObject(response.data.data)
      }
    } catch (error) {
      console.error('❌ 새 오브젝트 생성 실패:', error)
      alert('오브젝트 생성에 실패했습니다: ' + (error.response?.data?.message || error.message))
    } finally {
      setCreatingObject(false)
    }
  }

  // 페르소나 프롬프트 편집 모달 열기
  const openPersonaModal = () => {
    if (!selectedObject) {
      alert('먼저 오브젝트를 선택해주세요')
      return
    }
    setPersonaPrompt(selectedObject.persona_prompt || '')
    setShowPersonaModal(true)
  }

  // 페르소나 프롬프트 저장
  const savePersonaPrompt = async () => {
    if (!selectedObject) return

    setSavingPersona(true)
    try {
      const response = await axios.post('/ObjectUpload/update-persona-prompt', {
        objectId: selectedObject.id,
        personaPrompt: personaPrompt
      })

      if (response.data.success) {
        alert('✅ 페르소나 프롬프트가 저장되었습니다!')

        // 선택된 오브젝트 업데이트
        setSelectedObject({
          ...selectedObject,
          persona_prompt: personaPrompt
        })

        // 오브젝트 목록도 업데이트 (3D 탭의 arObjects)
        setArObjects(arObjects.map(obj =>
          obj.id === selectedObject.id
            ? { ...obj, persona_prompt: personaPrompt }
            : obj
        ))

        setShowPersonaModal(false)
      }
    } catch (error) {
      console.error('❌ 페르소나 프롬프트 저장 실패:', error)
      alert('페르소나 프롬프트 저장에 실패했습니다: ' + (error.response?.data?.error || error.message))
    } finally {
      setSavingPersona(false)
    }
  }

  // 2D 이미지 업로드 핸들러
  const handleImageUpload = async () => {
    if (!selectedFile || !selectedCategoryObject) return

    const newUploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setUploadId(newUploadId)
    setUploading(true)
    setUploadResult(null)
    setUploadProgress(null)

    const formData = new FormData()
    formData.append('file', selectedFile)
    formData.append('uploadId', newUploadId)

    try {
      const response = await axios.post('/ObjectUpload/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      const uploadedUrl = response.data?.data?.data?.files?.[0]?.url

      if (uploadedUrl) {
        console.log('업로드된 이미지 URL:', uploadedUrl)
        console.log('연결할 오브젝트 ID:', selectedCategoryObject.object_id)

        // 오브젝트에 이미지 URL 업데이트
        try {
          const updateResponse = await axios.post('/ObjectUpload/update-image-url', {
            objectId: selectedCategoryObject.object_id,
            imageUrl: uploadedUrl
          })

          console.log('✅ image_url 업데이트 성공:', updateResponse.data)

          // 업데이트 후 오브젝트 목록 다시 불러오기
          const refreshResponse = await axios.get(`/ObjectUpload/objects-by-category/${selectedCategory}`)
          if (refreshResponse.data.success) {
            setCategoryObjects(refreshResponse.data.objects)
            console.log('🔄 오브젝트 목록 업데이트 완료')
          }
        } catch (updateError) {
          console.error('❌ image_url 업데이트 실패:', updateError)
        }
      }

      setUploadResult({
        success: true,
        message: `이미지 업로드 성공! (오브젝트: ${selectedCategoryObject.name})`,
        data: response.data
      })
      setSelectedFile(null)

      setTimeout(() => {
        setUploadProgress(null)
      }, 2000)
    } catch (error) {
      setUploadResult({
        success: false,
        message: error.response?.data?.message || '이미지 업로드 실패',
        error: error.message
      })
    } finally {
      setUploading(false)
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
      }
    }
  }

  // GLB 다운로드 핸들러 (CORS 우회를 위해 서버 프록시 사용)
  const handleDownloadGLB = async (obj) => {
    if (!obj?.modelUrl) {
      alert('이 오브젝트는 등록된 3D 모델이 없습니다.')
      return
    }
    try {
      const proxyUrl = `/api/proxy/glb?url=${encodeURIComponent(obj.modelUrl)}`
      const res = await fetch(proxyUrl, { credentials: 'include' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const safeName = (obj.name || 'object').replace(/[\\/:*?"<>|]+/g, '_')
      link.download = `${safeName}.glb`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('GLB 다운로드 실패:', err)
      alert('GLB 다운로드에 실패했습니다: ' + err.message)
    }
  }

  // 문제세트에서 오브젝트 제거
  const handleRemoveObjectFromProblemSet = async (objectId) => {
    try {
      const response = await fetch('/auth/remove-object-from-problem-set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          problemSetId: selectedProblemSet,
          arObjectId: objectId
        })
      })

      const data = await response.json()
      console.log('🗑️ 오브젝트 제거 응답:', data)

      if (data.success) {
        alert('오브젝트가 문제세트에서 제거되었습니다.')

        // 오브젝트 목록 새로고침
        console.log('🔄 오브젝트 목록 새로고침 중...')
        const detailResponse = await fetch(`/auth/get-quiz-detail/${selectedProblemSet}`, {
          credentials: 'include'
        })

        const detailData = await detailResponse.json()
        console.log('📦 get-quiz-detail 응답:', detailData)

        if (detailData.success && detailData.quiz && detailData.quiz.problems) {
          // 고유 오브젝트 추출 (중복 제거)
          const objectsMap = new Map()
          detailData.quiz.problems.forEach(problem => {
            const objectId = problem.ar_object_id
            const objectName = problem.ar_object_name || problem.arObject || '알 수 없는 오브젝트'
            const objectImage = problem.ar_object_image
            const modelUrl = problem.model_url || null

            if (objectId && !objectsMap.has(objectId)) {
              objectsMap.set(objectId, {
                id: objectId,
                name: objectName,
                image: objectImage,
                modelUrl: modelUrl
              })
            }
          })

          const newObjects = Array.from(objectsMap.values())
          console.log('✅ 새로운 오브젝트 목록:', newObjects)
          setArObjects(newObjects)

          // 제거된 오브젝트가 선택되어 있었다면 선택 해제
          if (selectedObject?.id === objectId) {
            setSelectedObject(null)
            setSelectedFile(null)
          }
        } else {
          console.warn('⚠️ 예상과 다른 응답 구조:', detailData)
        }
      } else {
        alert(data.message || '오브젝트 제거에 실패했습니다.')
      }
    } catch (error) {
      console.error('오브젝트 제거 실패:', error)
      alert('오브젝트 제거 중 오류가 발생했습니다.')
    }
  }

  // 3D 모델 업로드 핸들러
  const handleUpload = async () => {
    if (!selectedFile || !selectedObject) return

    const newUploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setUploadId(newUploadId)
    setUploading(true)
    setUploadResult(null)
    setUploadProgress(null)

    const formData = new FormData()
    formData.append('file', selectedFile)
    formData.append('uploadId', newUploadId)

    try {
      const response = await axios.post('/ObjectUpload/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      const uploadedUrl = response.data?.data?.data?.files?.[0]?.url

      if (uploadedUrl) {
        console.log('업로드된 GLB URL:', uploadedUrl)
        console.log('연결할 오브젝트 ID:', selectedObject.id)

        // 오브젝트에 GLB URL 업데이트
        try {
          const updateResponse = await axios.post('/ObjectUpload/update-model-url', {
            objectId: selectedObject.id,
            modelUrl: uploadedUrl
          })

          console.log('✅ model_url 업데이트 성공:', updateResponse.data)

          // 업데이트 후 오브젝트 목록 다시 불러오기
          const detailResponse = await fetch(`/auth/get-quiz-detail/${selectedProblemSet}`, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            }
          })

          if (detailResponse.ok) {
            const detailData = await detailResponse.json()
            if (detailData.success && detailData.quiz && detailData.quiz.problems) {
              const objectsMap = new Map()
              detailData.quiz.problems.forEach(problem => {
                const objectId = problem.ar_object_id
                const objectName = problem.ar_object_name || problem.arObject || '알 수 없는 오브젝트'
                const objectImage = problem.ar_object_image
                const modelUrl = problem.model_url || null

                if (objectId && !objectsMap.has(objectId)) {
                  objectsMap.set(objectId, {
                    id: objectId,
                    name: objectName,
                    image: objectImage,
                    modelUrl: modelUrl
                  })
                }
              })

              const uniqueObjects = Array.from(objectsMap.values())
              setArObjects(uniqueObjects)
              console.log('🔄 오브젝트 목록 업데이트 완료')
            }
          }
        } catch (updateError) {
          console.error('❌ model_url 업데이트 실패:', updateError)
        }
      }

      setUploadResult({
        success: true,
        message: `파일 업로드 성공! (오브젝트: ${selectedObject.name})`,
        data: response.data
      })
      setSelectedFile(null)

      setTimeout(() => {
        setUploadProgress(null)
      }, 2000)
    } catch (error) {
      setUploadResult({
        success: false,
        message: error.response?.data?.message || '파일 업로드 실패',
        error: error.message
      })
    } finally {
      setUploading(false)
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
      }
    }
  }

  return (
    <Container theme={theme}>
      <Header>
        <Title theme={theme}>오브젝트 업로드</Title>
        <Subtitle theme={theme}>2D 이미지 또는 3D 모델을 업로드하세요</Subtitle>
      </Header>

      {/* 탭 */}
      <TabContainer theme={theme}>
        <Tab theme={theme} $active={activeTab === '2d'} onClick={() => setActiveTab('2d')}>
          <Image size={16} />
          2D 이미지 업로드
        </Tab>
        <Tab theme={theme} $active={activeTab === '3d'} onClick={() => setActiveTab('3d')}>
          <Box size={16} />
          3D 모델 업로드
        </Tab>
      </TabContainer>

      {/* 2D 이미지 업로드 탭 */}
      {activeTab === '2d' && (
        <>
          <InfoBox theme={theme}>
            <InfoTitle theme={theme}>
              <Info size={20} />
              2D 이미지 업로드
            </InfoTitle>
            <InfoText theme={theme}>
              • 카테고리 → 오브젝트를 선택한 후 이미지를 업로드하세요
            </InfoText>
            <InfoText theme={theme}>
              • 지원 형식: <InfoHighlight theme={theme}>JPG, PNG, GIF, WebP</InfoHighlight>
            </InfoText>
          </InfoBox>

          {categoryLoading ? (
            <LoadingSpinner size={48} color={theme.buttonPrimary} />
          ) : (
            <>
              {/* 1단계: 카테고리 선택 */}
              <SelectionSection>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <SectionLabel theme={theme} style={{ margin: 0 }}>1. 카테고리 선택</SectionLabel>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {selectedCategory && (
                      <Button
                        theme={theme}
                        onClick={handleDeleteCategory}
                        style={{
                          flex: 'none',
                          padding: '8px 16px',
                          fontSize: '14px',
                          background: '#ef4444',
                          color: 'white'
                        }}
                      >
                        🗑️ 카테고리 삭제
                      </Button>
                    )}
                    <Button
                      theme={theme}
                      $primary
                      onClick={() => setShowAddCategoryModal(true)}
                      style={{ flex: 'none', padding: '8px 16px', fontSize: '14px' }}
                    >
                      + 새 카테고리 추가
                    </Button>
                  </div>
                </div>
                <SelectBox
                  theme={theme}
                  value={selectedCategory || ''}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value || null)
                    setSelectedCategoryObject(null)
                    setSelectedFile(null)
                  }}
                >
                  <option value="">카테고리를 선택하세요</option>
                  {categories.map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {cat.name} - {cat.description}
                    </option>
                  ))}
                </SelectBox>
              </SelectionSection>

              {/* 2단계: 오브젝트 선택 */}
              {selectedCategory && (
                <SelectionSection>
                  <SectionLabel theme={theme}>2. 오브젝트 선택</SectionLabel>
                  <ObjectGrid>
                    {/* 새 오브젝트 추가 카드 */}
                    <AddObjectCard theme={theme} onClick={() => setShowAddModal(true)}>
                      <AddIcon theme={theme}>+</AddIcon>
                      <AddText theme={theme}>새 오브젝트 추가</AddText>
                    </AddObjectCard>

                    {/* 기존 오브젝트 목록 */}
                    {categoryObjects.map(obj => (
                      <ObjectCard
                        key={obj.object_id}
                        theme={theme}
                        $selected={selectedCategoryObject?.object_id === obj.object_id}
                        onClick={() => {
                          setSelectedCategoryObject(obj)
                          setSelectedFile(null)
                          setUploadResult(null)
                        }}
                      >
                        <PreviewSection>
                          <PreviewLabel theme={theme}>현재 이미지</PreviewLabel>
                          <ObjectImage $image={obj.image_url} theme={theme} />
                        </PreviewSection>
                        <ObjectName theme={theme}>{obj.name}</ObjectName>
                        <ObjectId theme={theme}>ID: {obj.object_id}</ObjectId>
                        <Button
                          theme={theme}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteObject(obj.object_id, obj.name)
                          }}
                          style={{
                            marginTop: '8px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            background: '#ef4444',
                            color: 'white'
                          }}
                        >
                          🗑️ 삭제
                        </Button>
                      </ObjectCard>
                    ))}
                  </ObjectGrid>
                </SelectionSection>
              )}

              {/* 3단계: 파일 업로드 */}
              {selectedCategoryObject && (
                <>
                  <SelectionSection>
                    <SectionLabel theme={theme}>
                      3. 이미지 파일 업로드 (선택된 오브젝트: {selectedCategoryObject.name})
                    </SectionLabel>
                  </SelectionSection>

                  <UploadArea
                    theme={theme}
                    $isDragOver={isDragOver}
                    $disabled={!selectedCategoryObject}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => selectedCategoryObject && fileInputRef.current?.click()}
                  >
                    <UploadIcon theme={theme} />
                    <UploadText theme={theme}>
                      클릭하거나 파일을 드래그하여 업로드
                    </UploadText>
                    <UploadSubtext theme={theme}>
                      JPG, PNG, GIF, WebP - 최대 10GB
                    </UploadSubtext>
                    <HiddenInput
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileInputChange}
                      disabled={!selectedCategoryObject}
                    />
                  </UploadArea>

                  {selectedFile && (
                    <FileList>
                      <FileItem theme={theme}>
                        <FileInfo>
                          <FileIcon theme={theme} />
                          <FileDetails>
                            <FileName theme={theme}>{selectedFile.name}</FileName>
                            <FileSize theme={theme}>{formatFileSize(selectedFile.size)}</FileSize>
                            {uploadProgress && (
                              <ProgressInfo theme={theme}>
                                {uploadProgress.status === 'uploading_to_server' && '서버 업로드 중...'}
                                {uploadProgress.uploadedBytes && uploadProgress.totalBytes &&
                                  ` ${formatFileSize(uploadProgress.uploadedBytes)} / ${formatFileSize(uploadProgress.totalBytes)}`
                                }
                              </ProgressInfo>
                            )}
                            {uploading && uploadProgress && (
                              <ProgressBar theme={theme}>
                                <ProgressFill $progress={uploadProgress.percentage || 0} />
                              </ProgressBar>
                            )}
                          </FileDetails>
                        </FileInfo>
                        <StatusIcon>
                          {uploading ? (
                            <Loader size={24} color={theme.buttonPrimary} className="spin" />
                          ) : (
                            <CheckCircle size={24} color={theme.buttonPrimary} />
                          )}
                        </StatusIcon>
                      </FileItem>

                      <UploadButton
                        onClick={handleImageUpload}
                        disabled={uploading}
                      >
                        {uploading
                          ? `업로드 중... ${uploadProgress?.percentage || 0}%`
                          : '업로드 시작'}
                      </UploadButton>
                    </FileList>
                  )}

                  {uploadResult && (
                    <ResultMessage $success={uploadResult.success}>
                      {uploadResult.message}
                      {uploadResult.success && uploadResult.data?.data && (
                        <ChunkInfo theme={theme}>
                          {uploadResult.data.data.data?.files && uploadResult.data.data.data.files.length > 0 && (
                            <div style={{ marginTop: '10px' }}>
                              <strong>업로드된 URL:</strong>
                              <ResultUrl
                                href={uploadResult.data.data.data.files[0].url}
                                target="_blank"
                                rel="noopener noreferrer"
                                theme={theme}
                              >
                                {uploadResult.data.data.data.files[0].url}
                              </ResultUrl>
                            </div>
                          )}
                        </ChunkInfo>
                      )}
                    </ResultMessage>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* 3D 모델 업로드 탭 */}
      {activeTab === '3d' && (
        <>
          <InfoBox theme={theme}>
            <InfoTitle theme={theme}>
              <Info size={20} />
              GLB 파일 업로드를 권장합니다
            </InfoTitle>
            <InfoText theme={theme}>
              • <InfoHighlight theme={theme}>GLB 포맷</InfoHighlight>으로 업로드하시면 게임에서 바로 사용할 수 있습니다
            </InfoText>
            <InfoText theme={theme}>
              • Blender에서 <InfoHighlight theme={theme}>File → Export → glTF 2.0 (.glb)</InfoHighlight>로 내보내기 가능
            </InfoText>
            <InfoText theme={theme}>
              • 리깅, 애니메이션, 텍스처가 모두 포함된 상태로 내보내주세요
            </InfoText>
          </InfoBox>

          {loading ? (
            <LoadingSpinner size={48} color={theme.buttonPrimary} />
          ) : (
            <>
              {/* 1단계: 문제세트 선택 */}
              <SelectionSection>
            <SectionLabel theme={theme}>1. 문제세트 선택</SectionLabel>
            <SelectBox
              theme={theme}
              value={selectedProblemSet || ''}
              onChange={(e) => {
                setSelectedProblemSet(e.target.value || null)
                setSelectedObject(null)
                setSelectedFile(null)
              }}
            >
              <option value="">문제세트를 선택하세요</option>
              {problemSets.map(set => (
                <option key={set.set_id} value={set.set_id}>
                  {set.set_title} (오브젝트 {set.set_obj_count}개)
                </option>
              ))}
            </SelectBox>
          </SelectionSection>

          {/* 2단계: 오브젝트 선택 */}
          {selectedProblemSet && (
            <SelectionSection>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <SectionLabel theme={theme}>2. 오브젝트 선택</SectionLabel>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => {
                      const selectedSet = problemSets.find(s => s.set_id === selectedProblemSet)
                      setSelectedProblemSetForAdd({
                        id: selectedProblemSet,
                        title: selectedSet?.set_title || '문제세트'
                      })
                      setShowAddObjectModal(true)
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    + 오브젝트 추가
                  </button>
                  <button
                    onClick={openPersonaModal}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    ✏️ 페르소나 등록
                  </button>
                </div>
              </div>
              {arObjects.length === 0 ? (
                <EmptyMessage theme={theme}>이 문제세트에는 오브젝트가 없습니다.</EmptyMessage>
              ) : (
                <ObjectGrid>
                  {arObjects.map(obj => (
                    <ObjectCard
                      key={obj.id}
                      theme={theme}
                      $selected={selectedObject?.id === obj.id}
                      onClick={() => {
                        setSelectedObject(obj)
                        setSelectedFile(null)
                        setUploadResult(null)
                      }}
                    >
                      {/* GLB 다운로드 + 제거 버튼 (modelUrl 있을 때만 다운로드 노출) */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          display: 'flex',
                          gap: '6px',
                          zIndex: 10
                        }}
                      >
                        {obj.modelUrl && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDownloadGLB(obj)
                            }}
                            title="GLB 다운로드"
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#2563eb',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                          >
                            <Download size={12} /> GLB
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (window.confirm(`"${obj.name}" 오브젝트를 이 문제세트에서 제거하시겠습니까?`)) {
                              handleRemoveObjectFromProblemSet(obj.id)
                            }
                          }}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#ff4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#cc0000'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#ff4444'}
                        >
                          제거
                        </button>
                      </div>

                      {/* 2D 이미지 미리보기 */}
                      <PreviewSection>
                        <PreviewLabel theme={theme}>2D 이미지</PreviewLabel>
                        <ObjectImage $image={obj.image} theme={theme} />
                      </PreviewSection>

                      {/* 3D 모델 미리보기 */}
                      <PreviewSection>
                        <PreviewLabel theme={theme}>3D 모델 (GLB)</PreviewLabel>
                        {obj.modelUrl ? (
                          <ModelPreview theme={theme}>
                            <model-viewer
                              src={obj.modelUrl}
                              auto-rotate
                              camera-controls
                              shadow-intensity="1"
                              alt={obj.name}
                            ></model-viewer>
                          </ModelPreview>
                        ) : (
                          <NoModelBadge theme={theme}>
                            업로드 필요
                          </NoModelBadge>
                        )}
                      </PreviewSection>

                      <ObjectName theme={theme}>{obj.name}</ObjectName>
                      <ObjectId theme={theme}>ID: {obj.id}</ObjectId>
                    </ObjectCard>
                  ))}
                </ObjectGrid>
              )}
            </SelectionSection>
          )}

          {/* 3단계: 파일 업로드 */}
          {selectedObject && (
            <>
              <SelectionSection>
                <SectionLabel theme={theme}>
                  3. GLB 파일 업로드 (선택된 오브젝트: {selectedObject.name})
                </SectionLabel>
              </SelectionSection>

              <UploadArea
                theme={theme}
                $isDragOver={isDragOver}
                $disabled={!selectedObject}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => selectedObject && fileInputRef.current?.click()}
              >
                <UploadIcon theme={theme} />
                <UploadText theme={theme}>
                  {selectedObject
                    ? '클릭하거나 파일을 드래그하여 업로드'
                    : '먼저 오브젝트를 선택하세요'}
                </UploadText>
                <UploadSubtext theme={theme}>
                  GLB 파일 권장 - 최대 10GB
                </UploadSubtext>
                <HiddenInput
                  ref={fileInputRef}
                  type="file"
                  accept=".glb,.gltf,.fbx,.obj"
                  onChange={handleFileInputChange}
                  disabled={!selectedObject}
                />
              </UploadArea>

              {selectedFile && (
                <FileList>
                  <FileItem theme={theme}>
                    <FileInfo>
                      <FileIcon theme={theme} />
                      <FileDetails>
                        <FileName theme={theme}>{selectedFile.name}</FileName>
                        <FileSize theme={theme}>{formatFileSize(selectedFile.size)}</FileSize>
                        {uploadProgress && (
                          <ProgressInfo theme={theme}>
                            청크 {uploadProgress.currentChunk}/{uploadProgress.totalChunks}
                            {uploadProgress.uploadedBytes && uploadProgress.totalBytes &&
                              ` - ${formatFileSize(uploadProgress.uploadedBytes)} / ${formatFileSize(uploadProgress.totalBytes)}`
                            }
                          </ProgressInfo>
                        )}
                        {uploading && uploadProgress && (
                          <ProgressBar theme={theme}>
                            <ProgressFill $progress={uploadProgress.percentage || 0} />
                          </ProgressBar>
                        )}
                      </FileDetails>
                    </FileInfo>
                    <StatusIcon>
                      {uploading ? (
                        <Loader size={24} color={theme.buttonPrimary} className="spin" />
                      ) : (
                        <CheckCircle size={24} color={theme.buttonPrimary} />
                      )}
                    </StatusIcon>
                  </FileItem>

                  <UploadButton
                    onClick={handleUpload}
                    disabled={uploading}
                  >
                    {uploading
                      ? `업로드 중... ${uploadProgress?.percentage || 0}%`
                      : '업로드 시작'}
                  </UploadButton>
                </FileList>
              )}

              {uploadResult && (
                <ResultMessage $success={uploadResult.success}>
                  {uploadResult.message}
                  {uploadResult.success && uploadResult.data?.data && (
                    <ChunkInfo theme={theme}>
                      {uploadResult.data.data.data?.files && uploadResult.data.data.data.files.length > 0 && (
                        <div style={{ marginTop: '10px' }}>
                          <strong>업로드된 URL:</strong>
                          <ResultUrl
                            href={uploadResult.data.data.data.files[0].url}
                            target="_blank"
                            rel="noopener noreferrer"
                            theme={theme}
                          >
                            {uploadResult.data.data.data.files[0].url}
                          </ResultUrl>
                        </div>
                      )}
                    </ChunkInfo>
                  )}
                </ResultMessage>
              )}
            </>
          )}
            </>
          )}
        </>
      )}

      {/* 새 카테고리 추가 모달 */}
      {showAddCategoryModal && (
        <Modal onClick={() => setShowAddCategoryModal(false)}>
          <ModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
            <ModalTitle theme={theme}>새 카테고리 추가</ModalTitle>

            <div>
              <InputLabel theme={theme}>카테고리 분류 *</InputLabel>
              <Input
                theme={theme}
                type="text"
                placeholder="예: 역사"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                disabled={creatingCategory}
              />
            </div>

            <div>
              <InputLabel theme={theme}>카테고리 세부분류 *</InputLabel>
              <TextArea
                theme={theme}
                placeholder="예: 중세 시대"
                value={newCategoryDescription}
                onChange={(e) => setNewCategoryDescription(e.target.value)}
                disabled={creatingCategory}
              />
            </div>

            <ButtonGroup>
              <Button theme={theme} onClick={() => setShowAddCategoryModal(false)} disabled={creatingCategory}>
                취소
              </Button>
              <Button
                theme={theme}
                $primary
                onClick={handleCreateCategory}
                disabled={!newCategoryName.trim() || !newCategoryDescription.trim() || creatingCategory}
              >
                {creatingCategory ? '생성 중...' : '생성하기'}
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* 새 오브젝트 추가 모달 */}
      {showAddModal && (
        <Modal onClick={() => setShowAddModal(false)}>
          <ModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
            <ModalTitle theme={theme}>새 오브젝트 추가</ModalTitle>

            <div>
              <InputLabel theme={theme}>오브젝트 이름 *</InputLabel>
              <Input
                theme={theme}
                type="text"
                placeholder="예: 훈민정음"
                value={newObjectName}
                onChange={(e) => setNewObjectName(e.target.value)}
                disabled={creatingObject}
              />
            </div>

            <div>
              <InputLabel theme={theme}>설명</InputLabel>
              <TextArea
                theme={theme}
                placeholder="예: 세종대왕이 창제한 한글"
                value={newObjectDescription}
                onChange={(e) => setNewObjectDescription(e.target.value)}
                disabled={creatingObject}
              />
            </div>

            <ButtonGroup>
              <Button theme={theme} onClick={() => setShowAddModal(false)} disabled={creatingObject}>
                취소
              </Button>
              <Button
                theme={theme}
                $primary
                onClick={handleCreateObject}
                disabled={!newObjectName.trim() || creatingObject}
              >
                {creatingObject ? '생성 중...' : '생성하기'}
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* 오브젝트 추가 모달 */}
      <AddObjectToProblemSetModal
        open={showAddObjectModal}
        onClose={() => setShowAddObjectModal(false)}
        problemSetId={selectedProblemSetForAdd?.id}
        problemSetTitle={selectedProblemSetForAdd?.title}
        onSuccess={async () => {
          // 오브젝트 목록 새로고침
          console.log('🔄 onSuccess 콜백 실행 - 오브젝트 목록 새로고침 중...')
          console.log('🔍 selectedProblemSet:', selectedProblemSet)
          try {
            const response = await fetch(`/auth/get-quiz-detail/${selectedProblemSet}`, {
              credentials: 'include'
            })
            console.log('📡 get-quiz-detail 응답:', response.status)
            const data = await response.json()
            console.log('📦 get-quiz-detail 데이터:', data)

            if (data.success && data.quiz && data.quiz.problems) {
              console.log('📋 문제 개수:', data.quiz.problems.length)
              const objectsMap = new Map()
              data.quiz.problems.forEach(problem => {
                const objectId = problem.ar_object_id
                const objectName = problem.ar_object_name || problem.arObject || '알 수 없는 오브젝트'
                const objectImage = problem.ar_object_image
                const modelUrl = problem.model_url || null

                if (objectId && !objectsMap.has(objectId)) {
                  objectsMap.set(objectId, {
                    id: objectId,
                    name: objectName,
                    image: objectImage,
                    modelUrl: modelUrl
                  })
                }
              })
              const newObjects = Array.from(objectsMap.values())
              console.log('✅ 새로운 오브젝트 목록 (중복 제거):', newObjects)
              setArObjects(newObjects)
              console.log('✅ setArObjects 호출 완료')
            } else {
              console.warn('⚠️ 데이터 구조가 예상과 다름:', data)
            }
          } catch (error) {
            console.error('❌ 오브젝트 목록 새로고침 실패:', error)
          }
        }}
      />

      {/* 페르소나 프롬프트 설정 모달 */}
      {showPersonaModal && (
        <Modal onClick={() => setShowPersonaModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <div style={{ marginBottom: '20px' }}>
              <SectionLabel theme={theme}>페르소나 프롬프트 설정</SectionLabel>
              {selectedObject && (
                <div style={{ marginTop: '10px', fontSize: '14px', color: theme.text }}>
                  <strong>오브젝트:</strong> {selectedObject.name} (ID: {selectedObject.id})
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: theme.text }}>
                페르소나 프롬프트
              </label>
              <TextArea
                theme={theme}
                placeholder="예: 너는 조선시대 세종대왕이야. 한글을 창제한 위대한 군주로서 학생들에게 한글의 우수성과 창제 과정을 알려줘."
                value={personaPrompt}
                onChange={(e) => setPersonaPrompt(e.target.value)}
                disabled={savingPersona}
                style={{ minHeight: '150px', fontSize: '14px' }}
              />
              <div style={{ marginTop: '8px', fontSize: '12px', color: theme.textSecondary }}>
                * 이 프롬프트는 AI 캐릭터의 성격과 대화 방식을 정의합니다.
              </div>
            </div>

            <ButtonGroup>
              <Button theme={theme} onClick={() => setShowPersonaModal(false)} disabled={savingPersona}>
                취소
              </Button>
              <Button
                theme={theme}
                $primary
                onClick={savePersonaPrompt}
                disabled={savingPersona}
              >
                {savingPersona ? '저장 중...' : '저장하기'}
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  )
}

export default ObjectUploadPage
